import React from "react";
import { Grid, useMediaQuery } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { loadDistributionsByAssetClass } from "redux/distributions/distributionsFunctions";
import { getReportDateSelector } from "redux/user/user";
import { RootState } from "redux/rootReducer";
import PieChart from "components/PieChart";
import { AssetClass } from "types/AssetClass";

interface Props {
  assetClass: AssetClass;
}

const Distributions = (props: Props) => {
  const { assetClass } = props;

  const { t } = useTranslation();

  const matchesMediaQuery = useMediaQuery("(min-width: 960px)");

  const dispatch = useDispatch();

  const reportControlId = useSelector(
    (state: RootState) => state.user.reportControlId
  );
  const reportDate = useSelector(getReportDateSelector);

  const distribution1 = useSelector(
    (state: RootState) =>
      state.distributions[1]?.[
        AssetClass[assetClass] as keyof typeof AssetClass
      ]?.data ?? []
  );
  const distribution1Loading = useSelector(
    (state: RootState) =>
      state.distributions[1]?.[
        AssetClass[assetClass] as keyof typeof AssetClass
      ]?.loading
  );
  const distribution2 = useSelector(
    (state: RootState) =>
      state.distributions[2]?.[
        AssetClass[assetClass] as keyof typeof AssetClass
      ]?.data ?? []
  );
  const distribution2Loading = useSelector(
    (state: RootState) =>
      state.distributions[2]?.[
        AssetClass[assetClass] as keyof typeof AssetClass
      ]?.loading
  );
  const distribution3 = useSelector(
    (state: RootState) =>
      state.distributions[3]?.[
        AssetClass[assetClass] as keyof typeof AssetClass
      ]?.data ?? []
  );
  const distribution3Loading = useSelector(
    (state: RootState) =>
      state.distributions[3]?.[
        AssetClass[assetClass] as keyof typeof AssetClass
      ]?.loading
  );

  React.useEffect(() => {
    if (reportControlId && reportDate) {
      dispatch(
        loadDistributionsByAssetClass(
          1,
          assetClass,
          reportControlId,
          reportDate
        )
      );
      dispatch(
        loadDistributionsByAssetClass(
          2,
          assetClass,
          reportControlId,
          reportDate
        )
      );
      dispatch(
        loadDistributionsByAssetClass(
          3,
          assetClass,
          reportControlId,
          reportDate
        )
      );
    }
  }, [dispatch, reportControlId, reportDate, assetClass]);

  return (
    <>
      <Grid
        item
        md={4}
        sm={12}
        style={{
          borderRight: matchesMediaQuery ? "1px solid #D7D7D7" : "none",
          borderBottom: matchesMediaQuery ? "none" : "1px solid #D7D7D7",
          marginBottom: 32,
          padding: matchesMediaQuery ? "0 16px" : 0,
        }}
      >
        <PieChart
          data={distribution1.map((d) => ({
            name: d.Category,
            value: d.Value,
          }))}
          label={t("location")}
          loading={distribution1Loading}
        />
      </Grid>
      <Grid
        item
        md={4}
        sm={12}
        style={{
          borderRight: matchesMediaQuery ? "1px solid #D7D7D7" : "none",
          borderBottom: matchesMediaQuery ? "none" : "1px solid #D7D7D7",
          marginBottom: 32,
          padding: matchesMediaQuery ? "0 16px" : 0,
        }}
      >
        <PieChart
          data={distribution2.map((d) => ({
            name: d.Category,
            value: d.Value,
          }))}
          label={t("currency")}
          loading={distribution2Loading}
        />
      </Grid>
      <Grid
        item
        md={4}
        sm={12}
        style={{ marginBottom: 32, padding: matchesMediaQuery ? "0 16px" : 0 }}
      >
        <PieChart
          data={distribution3.map((d) => ({
            name: d.Category,
            value: d.Value,
          }))}
          label={t("wealth_distribution")}
          loading={distribution3Loading}
        />
      </Grid>
    </>
  );
};

export default React.memo(Distributions);
